/**
 * Registry of built-in notification sounds + per-channel sound settings.
 *
 * React-free so it can be imported by the outside-React firehose ingest path.
 * `BUILTIN_SOUNDS` is the single place to register sounds — add one entry (with
 * an `import` above) and it appears in every channel's picker automatically.
 *
 * See `src/assets/sounds/CREDITS.md` for sound provenance/licensing.
 */

import chimeSound from "@/assets/sounds/chime.mp3";
import spoonSound from "@/assets/sounds/spoon.mp3";
import risingPopSound from "@/assets/sounds/rising-pops.mp3";
import tapSound from "@/assets/sounds/tap-notification.mp3";
import beepSound from "@/assets/sounds/beep.mp3";
import bellNotification from "@/assets/sounds/bell-notification.mp3";
import bubblePopAlert from "@/assets/sounds/bubble-pop-alert.mp3";
import confirmationTone from "@/assets/sounds/confirmation-tone.mp3";
import doorbellTone from "@/assets/sounds/doorbell-tone.mp3";
import waveAlarm from "@/assets/sounds/wave-alarm.mp3";
import interfaceSelect from "@/assets/sounds/interface-select.mp3";
import positiveNotification from "@/assets/sounds/positive-notification.mp3";
import interfaceRemove from "@/assets/sounds/interface-remove.mp3";
import interfaceStart from "@/assets/sounds/interface-start.mp3";

export type BuiltinSound = {
  id: string;
  label: string;
  src: string;
};

export const BUILTIN_SOUNDS: BuiltinSound[] = [
  // Pre-existing / generated defaults
  { id: "chime", label: "Chime", src: chimeSound },
  { id: "spoon", label: "Spoon", src: spoonSound },
  { id: "beep", label: "Beep", src: beepSound },
  { id: "tap", label: "Tap", src: tapSound },
  { id: "rising-pops", label: "Rising Pops", src: risingPopSound },
  // Mixkit library (Mixkit Free License)
  { id: "bell-notification", label: "Bell Notification", src: bellNotification },
  { id: "bubble-pop-alert", label: "Bubble Pop Alert", src: bubblePopAlert },
  { id: "confirmation-tone", label: "Confirmation Tone", src: confirmationTone },
  { id: "doorbell-tone", label: "Doorbell Tone", src: doorbellTone },
  { id: "wave-alarm", label: "Wave Alarm", src: waveAlarm },
  { id: "interface-select", label: "Interface Select", src: interfaceSelect },
  { id: "positive-notification", label: "Positive Notification", src: positiveNotification },
  { id: "interface-remove", label: "Interface Remove", src: interfaceRemove },
  { id: "interface-start", label: "Interface Start", src: interfaceStart },
];

export type NotificationSoundId = string;

export type ChannelSound = {
  soundId: NotificationSoundId;
  /** 0–1 */
  volume: number;
};

export type NotificationChannel = "headlines" | "truthSocial" | "portfolio";

/**
 * Defaults preserve today's behaviour exactly: headlines=chime, portfolio=spoon,
 * truthSocial=beep (the regenerated 880 Hz tone), all at 0.5.
 */
export const DEFAULT_NOTIFICATION_SOUND_SETTINGS: Record<
  NotificationChannel,
  ChannelSound
> = {
  headlines: { soundId: "chime", volume: 0.5 },
  truthSocial: { soundId: "beep", volume: 0.5 },
  portfolio: { soundId: "spoon", volume: 0.5 },
};

const SOUND_BY_ID: Record<string, BuiltinSound> = Object.fromEntries(
  BUILTIN_SOUNDS.map((s) => [s.id, s])
);

/**
 * Resolves a sound id to its registry entry, falling back to the given default
 * id (then the first registered sound) if the id is unknown — e.g. a stored
 * setting references a sound that was later removed.
 */
export const resolveSound = (
  soundId: string,
  fallbackId = "chime"
): BuiltinSound =>
  SOUND_BY_ID[soundId] ?? SOUND_BY_ID[fallbackId] ?? BUILTIN_SOUNDS[0];
